/* eslint-disable react/no-array-index-key */
import React, { useEffect } from 'react';
import { useMemo, Fragment } from 'react';

// material-ui
import { Stack, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

// third-party
import {
    useFilters,
    useGlobalFilter,
    usePagination,
    useRowSelect,
    useSortBy,
    useTable
} from 'react-table';

// project import
import {
    HeaderSort,
    SelectionCell,
    SelectionHeader,
    TablePagination,
    TableRowSelection
} from 'components/third-party/ReactTable';
import { renderFilterTypes, DefaultColumnFilter } from 'utils/react-table';
import { getDateFromTimestamp } from 'utils/Helpers';
import { usePermissions } from 'hooks/usePermissions';
import { OrgPermissions } from 'utils/permissions';

// ==============================|| SAMPLE PAGE ||============================== //

const CodesTable = ({
    codes,
    tPage,
    tRow,
    setTableIndex,
    setRowCount,
    changingFocus,
    onCodeClick,
    onSelectionChange,
    globalFilter: externalGlobalFilter
}) => {
    const { hasPermission } = usePermissions();
    const canManageOrgCodes = hasPermission(OrgPermissions.MANAGE_ORG_CODES);

    const data = useMemo(
        () =>
            codes?.length
                ? codes?.map(item => {
                      return {
                          ...item,
                          expiresAt: getDateFromTimestamp(item.expires_at.seconds)
                      };
                  })
                : [],
        [codes]
    );

    const columns = useMemo(
        () => [
            {
                Header: 'Name',
                accessor: 'name'
            },
            {
                Header: 'Code',
                accessor: 'code'
            },
            {
                Header: 'Expires',
                accessor: 'expiresAt'
            }
        ],
        []
    );

    const theme = useTheme();

    const filterTypes = useMemo(() => renderFilterTypes, []);
    const defaultColumn = useMemo(() => ({ Filter: DefaultColumnFilter }), []);
    const initialState = useMemo(
        () => ({
            filters: [
                { id: 'email', value: '' },
                { id: 'name', value: '' }
            ],
            pageIndex: tPage,
            pageSize: tRow,
            sortBy: [
                {
                    id: 'first_name',
                    desc: false
                }
            ]
        }),
        [tPage, tRow]
    );
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        page,
        prepareRow,
        gotoPage,
        setPageSize,
        state: { pageIndex, pageSize, selectedRowIds },
        selectedFlatRows,
        setGlobalFilter: setInternalGlobalFilter
    } = useTable(
        {
            columns,
            data,
            defaultColumn,
            initialState,
            filterTypes
        },
        useGlobalFilter,
        useFilters,
        useSortBy,
        usePagination,
        useRowSelect,
        hooks => {
            hooks.allColumns.push(columns => [
                {
                    id: 'row-selection-chk',
                    accessor: 'Selection',
                    Header: headerProps => {
                        return canManageOrgCodes ? (
                            <SelectionHeader {...headerProps} />
                        ) : null;
                    },
                    Cell: rowCell => {
                        return canManageOrgCodes ? <SelectionCell {...rowCell} /> : null;
                    }
                },
                ...columns
            ]);
        }
    );

    useEffect(() => {
        if (typeof externalGlobalFilter !== 'undefined') {
            setInternalGlobalFilter(externalGlobalFilter);
        }
    }, [externalGlobalFilter, setInternalGlobalFilter]);

    useEffect(() => {
        if (onSelectionChange) {
            onSelectionChange(selectedFlatRows.map(row => row.original));
        }
    }, [selectedRowIds, onSelectionChange, selectedFlatRows]);

    useEffect(() => {
        if (changingFocus) {
            gotoPage(0);
            setPageSize(10);
        }
    }, [changingFocus, gotoPage, setPageSize]);

    return (
        <Stack>
            {/* <Stack
                direction={matchDownSM ? 'column' : 'row'}
                spacing={1}
                justifyContent="space-between"
                alignItems="center"
                sx={{ pb: 3 }}
            >
                <Stack direction={matchDownSM ? 'column' : 'row'} spacing={2}>
                    <GlobalFilter
                        preGlobalFilteredRows={preGlobalFilteredRows}
                        globalFilter={globalFilter}
                        setGlobalFilter={setGlobalFilter}
                        size="small"
                    />
                </Stack>
            </Stack> */}
            {canManageOrgCodes && (
                <TableRowSelection selected={Object.keys(selectedRowIds).length} />
            )}
            <Table {...getTableProps()}>
                <TableHead sx={{ borderTopWidth: 1 }}>
                    {headerGroups.map((headerGroup, index) => (
                        <TableRow {...headerGroup.getHeaderGroupProps()} key={index}>
                            {headerGroup.headers.map((column, index) => (
                                <TableCell
                                    {...column.getHeaderProps([
                                        { className: column.className },
                                        column => column.getSortByToggleProps()
                                    ])}
                                    sx={{
                                        width: 'auto'
                                    }}
                                    key={index}
                                >
                                    {(column.id === 'name' ||
                                        column.id === 'expires_at') && (
                                        <HeaderSort column={column} sort />
                                    )}
                                    {!(
                                        column.id === 'name' || column.id === 'expires_at'
                                    ) && column.render('Header')}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableHead>
                <TableBody {...getTableBodyProps()}>
                    {headerGroups.map((group, i) => (
                        <TableRow {...group.getHeaderGroupProps()} key={i}>
                            {group.headers.map((column, index) => (
                                <TableCell
                                    {...column.getHeaderProps([
                                        { className: column.className }
                                    ])}
                                    key={index}
                                />
                            ))}
                        </TableRow>
                    ))}
                    {page.map((row, i) => {
                        prepareRow(row);
                        return (
                            <Fragment key={i}>
                                <TableRow
                                    {...row.getRowProps()}
                                    onClick={() => onCodeClick(row.original)}
                                    sx={{
                                        cursor: 'pointer',
                                        bgcolor: row.isSelected
                                            ? alpha(theme.palette.primary.lighter, 0.35)
                                            : 'inherit'
                                    }}
                                    key={i}
                                >
                                    {row.cells.map((cell, i) => (
                                        <TableCell
                                            {...cell.getCellProps([
                                                { className: cell.column.className }
                                            ])}
                                            sx={{
                                                ...(cell.column.id === 'name' && {
                                                    textTransform: 'capitalize'
                                                })
                                            }}
                                            key={i}
                                        >
                                            {cell.render('Cell')}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </Fragment>
                        );
                    })}

                    <TableRow>
                        <TableCell sx={{ p: 2 }} colSpan={7}>
                            <TablePagination
                                gotoPage={gotoPage}
                                rows={rows}
                                setPageSize={setPageSize}
                                pageIndex={pageIndex}
                                pageSize={pageSize}
                                setTableIndex={setTableIndex}
                                setRowCount={setRowCount}
                                type="otc"
                            />
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </Stack>
    );
};

export default CodesTable;
